'use client';

import { FC, MutableRefObject, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Search, LayoutGrid, Columns2, Map as MapIcon, X } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { Carousel } from '@/hotdesk-ui/enum/listing-workspace';
import SearchSuggestionsDropdown from '@/hotdesk-ui/components/shared/SearchSuggestionsDropdown';
import SuggestedDestinations from '@/hotdesk-ui/components/shared/SuggestedDestinations';
import { useSearchAutocomplete } from '@/hotdesk-ui/hooks/useSearchAutocomplete';
import { useRecentSearches } from '@/hotdesk-ui/hooks/useRecentSearches';
import { RecentSearch } from '@/hotdesk-ui/helpers/recent-searches';
import { useAppDispatch, useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { fetchSuggestedCities, SuggestedCity } from '@/hotdesk-ui/store/slices/suggested-cities.slice';
import { GeoCodeResult } from '@/hotdesk-ui/types/geo-code-result.type';
import styles from './ExploreToolbar.module.scss';
export type ViewMode = Carousel;

/**
 * Above this many results the exact total is replaced with "Over N …" — the
 * count stops being meaningful once the radius pulls in surrounding areas.
 * At or below it we show the real number, correctly pluralised. Tune freely:
 * nothing else depends on the value being 1,000.
 */
const RESULTS_COUNT_CAP = 1000;
export interface ExploreToolbarProps {
  autocompleteInput: MutableRefObject<any>;
  autocompleteContainer: MutableRefObject<any>;
  onSearch: () => void;
  onClearSearch: () => void;
  /** "Use my location" — fired by the suggestions panel's Nearby row. */
  onDetectLocation: () => void;
  viewMode: ViewMode;
  onChangeView: (mode: ViewMode) => void;
  totalCount: number;
  /** A page-1 fetch is in flight — the count on screen is already stale. */
  listRefreshing?: boolean;
  /** Drives the heading noun: "workspaces" vs "spaces". */
  isWorkspaceMode?: boolean;
  placeholder?: string;
  searchDisplayKey?: string;
  sourceOfLocationData?: string;
  /** Source of the *current* geoLocation ('user-geolocation' => show "Nearby"). */
  geoLocationSource?: string;
  /** Called when a place is picked from the suggestions dropdown. */
  onSelect: (result: GeoCodeResult) => void;
  /** Rendered above the results row — e.g. long-term / memberships banners. */
  children?: ReactNode;
  /** Compare button, beside the view switch (left of it on desktop, right on mobile). */
  compareSlot?: ReactNode;
}
const ExploreToolbar: FC<ExploreToolbarProps> = ({
  autocompleteInput,
  autocompleteContainer,
  onSearch,
  onClearSearch,
  onDetectLocation,
  viewMode,
  onChangeView,
  totalCount,
  listRefreshing = false,
  isWorkspaceMode = false,
  placeholder,
  searchDisplayKey,
  onSelect,
  children,
  compareSlot
}) => {
  const t = useTranslations();
  const tV4 = useTranslations('landing-v4');
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isStuck, setIsStuck] = useState(false);
  const [hasSearchValue, setHasSearchValue] = useState(false);
  const dispatch = useAppDispatch();
  const suggestedCities = useAppSelector((state: RootState) => state.suggestedCities.items);
  const suggestedCitiesLoading = useAppSelector((state: RootState) => state.suggestedCities.loading);
  const geoData = useAppSelector((state: RootState) => state.geoLocation.geoLocationZipData);
  const currentCity = geoData?.city;
  const countryCode = geoData?.country_sort;
  const {
    recent,
    record,
    clear: clearRecent
  } = useRecentSearches();

  // Every resolved place funnels through here so it is recorded once —
  // autocomplete picks land here via the hook, panel rows call it directly.
  const handleSelect = useCallback((result: GeoCodeResult) => {
    record(result);
    onSelect(result);
  }, [record, onSelect]);
  const {
    containerRef,
    predictions,
    activeIndex,
    isOpen,
    isEmptyFocus,
    setActiveIndex,
    selectByIndex,
    selectActiveOrFirst,
    close,
    resetToEmpty
  } = useSearchAutocomplete({
    inputRef: autocompleteInput,
    onSelect: handleSelect
  });

  // Warm the suggestions once the country is known, so the empty-focus panel
  // has content the first time the input is focused.
  useEffect(() => {
    if (countryCode) dispatch(fetchSuggestedCities(countryCode));
  }, [countryCode, dispatch]);

  // Panel rows aren't Google predictions; build the same GeoCodeResult shape
  // and push it down the normal path.
  const selectPlace = useCallback((place: {
    label: string;
    lat: number;
    lng: number;
    city?: string;
    country?: string;
    countryCode?: string;
    placeId?: string;
    /** Country-wide row — must carry the `country` type through. */
    isCountry?: boolean;
    /** State-wide row — its English name, searched as a state. */
    regionName?: string;
  }) => {
    if (autocompleteInput.current) {
      autocompleteInput.current.value = place.label;
    }
    setHasSearchValue(true);
    close();
    handleSelect({
      address_components: [] as unknown as GeoCodeResult['address_components'],
      formatted_address: place.label,
      geometry: {
        location: {
          lat: place.lat,
          lng: place.lng
        },
        location_type: 'APPROXIMATE'
      },
      place_id: place.placeId || '',
      plus_code: {
        compound_code: '',
        global_code: ''
      },
      // Everything downstream (fetch by country name, `countryCode`
      // in the URL, map framing) keys off this type.
      types: (place.isCountry ? ['country'] : place.regionName ? ['administrative_area_level_1'] : []) as unknown as GeoCodeResult['types'],
      country: place.country,
      country_sort: place.countryCode,
      city: place.city,
      ...(place.regionName ? {
        state: place.regionName,
        region_name: place.regionName
      } : {}),
      lat: place.lat,
      log: place.lng,
      source: 'autocomplete'
    } as GeoCodeResult);
  }, [autocompleteInput, close, handleSelect]);
  const handleSuggestedCity = useCallback((city: SuggestedCity) => selectPlace({
    label: city.countryName ? `${city.name}, ${city.countryName}` : city.name,
    lat: city.lat,
    lng: city.lng,
    city: city.name,
    country: city.countryName,
    countryCode
  }), [selectPlace, countryCode]);
  const handleRecentSearch = useCallback((entry: RecentSearch) => selectPlace({
    label: entry.label,
    lat: entry.lat,
    lng: entry.lng,
    city: entry.city,
    country: entry.country,
    countryCode: entry.countryCode,
    placeId: entry.id.includes(',') ? undefined : entry.id,
    isCountry: entry.isCountry,
    regionName: entry.regionName
  }), [selectPlace]);

  // Search button: if the user typed but didn't pick a suggestion, choose the
  // highlighted/first one; otherwise refetch the current location.
  const handleSearchClick = () => {
    if (!selectActiveOrFirst()) onSearch();
  };
  const syncSearchValue = useCallback(() => {
    const value = autocompleteInput.current?.value?.trim() ?? '';
    setHasSearchValue(value.length > 0);
  }, [autocompleteInput]);
  useEffect(() => {
    syncSearchValue();
  }, [searchDisplayKey, syncSearchValue]);
  const handleClearSearch = () => {
    if (autocompleteInput.current) {
      autocompleteInput.current.value = '';
    }
    setHasSearchValue(false);
    onClearSearch();
    autocompleteInput.current?.focus();
    // Drop the stale predictions and fall back to suggested destinations.
    resetToEmpty();
  };

  /**
   * The button beside the search icon does double duty, matching
   * HeaderSearch. With something typed it clears; with nothing typed there
   * is nothing to clear, so it dismisses the suggestions panel instead — on
   * mobile that panel fills the screen and otherwise offers no visible way
   * out. Typing swaps it straight back to the clear button. Restricted to
   * mobile in CSS: on desktop the panel is a dropdown with the page still
   * visible around it.
   */
  const panelOpen = isOpen || isEmptyFocus;
  const closesPanel = !hasSearchValue && panelOpen;
  const actionVisible = hasSearchValue || closesPanel;
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(([entry]) => setIsStuck(!entry.isIntersecting), {
      rootMargin: '-80px 0px 0px 0px',
      threshold: 0
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  // "12 spaces" / "1 workspace" — or "Over 1,000 spaces" once the total is
  // large enough that the exact figure stops being useful. Pluralisation
  // lives in the ICU message so every locale gets its own rules.
  const isOverCap = totalCount > RESULTS_COUNT_CAP;
  const countLabel = isOverCap ? t(isWorkspaceMode ? 'explore.resultsOverWorkspaces' : 'explore.resultsOverSpaces', {
    count: RESULTS_COUNT_CAP
  }) : t(isWorkspaceMode ? 'explore.resultsCountWorkspaces' : 'explore.resultsCountSpaces', {
    count: totalCount
  });
  // The heading is the count only, so the row keeps room for the Compare
  // button and the view switch.
  const resultsHeading = countLabel;
  return <>
            <div className={styles.toolbar}>
                <div className={styles.searchRow} ref={containerRef}>
                    {/* Mirrors the landing search bar: a single "Where" segment
                        with the mini-label, a clear button, then the black
                        round search button. */}
                    <div className={styles.searchBar} ref={autocompleteContainer}>
                        <div className={styles.searchSegment}>
                            <label className={styles.textContainer}>
                                <span className={styles.segmentLabel}>
                                    {tV4('search.whereLabel')}
                                </span>
                                <input ref={autocompleteInput} type="text" className={styles.searchInput} placeholder={placeholder || tV4('search.wherePlaceholder')} autoCorrect="off" autoCapitalize="off" spellCheck={false} onInput={syncSearchValue} onChange={syncSearchValue} />
                            </label>
                            <button type="button" className={`${styles.clearBtn} ${actionVisible ? styles.clearBtnVisible : ''} ${closesPanel ? styles.clearBtnClosesPanel : ''}`} onClick={closesPanel ? close : handleClearSearch} aria-label={closesPanel ? t('explore.close') : t('explore.clearSearch')} aria-hidden={!actionVisible} tabIndex={actionVisible ? 0 : -1}>
                                <X size={16} strokeWidth={2.25} data-sentry-element="X" data-sentry-source-file="ExploreToolbar.tsx" />
                            </button>
                        </div>
                        <button type="button" className={styles.searchButton} onClick={handleSearchClick} aria-label="Search">
                            <Search size={18} color="#fff" data-sentry-element="Search" data-sentry-source-file="ExploreToolbar.tsx" />
                        </button>
                    </div>
                    {isOpen && <SearchSuggestionsDropdown predictions={predictions} activeIndex={activeIndex} onHover={setActiveIndex} onSelect={selectByIndex} />}

                    {/* Nothing typed yet — offer cities in the visitor's
                        country plus recent searches, same as the landing. */}
                    {!isOpen && isEmptyFocus && <SuggestedDestinations cities={suggestedCities} loading={suggestedCitiesLoading} currentCity={currentCity} onSelect={handleSuggestedCity} onUseMyLocation={() => {
          close();
          onDetectLocation();
        }} recent={recent} onSelectRecent={handleRecentSearch} onClearRecent={clearRecent} />}
                </div>
            </div>

            {children}

            <div ref={sentinelRef} className={styles.stickySentinel} aria-hidden="true" />
            <div data-explore-results-row className={`${styles.resultsRow} ${viewMode === Carousel.split ? styles.resultsRowSplit : ''} ${isStuck ? styles.resultsRowStuck : ''}`}>
                <div className={styles.resultsRowInner}>
                    {/* Transparent to the grid outside split view; in split it
                        holds heading + toggle over the list column. */}
                    <div className={styles.resultsMain}>
                    {/* `totalCount` is still the previous search's while the
                        next one is in flight — shimmer rather than assert a
                        count the results below no longer match. */}
                    {listRefreshing ? <span className={styles.resultsHeadingSkeleton} aria-hidden="true" /> : <h1 className={styles.resultsHeading}>{resultsHeading}</h1>}
                    <div className={styles.viewToggleWrap}>
                        {compareSlot && <div className={styles.compareSlot}>{compareSlot}</div>}
                        <span className={styles.viewLabel}>
                            {t('explore.view.label')}
                        </span>
                        <div className={styles.viewToggle} role="tablist">
                            <button type="button" role="tab" aria-selected={viewMode === Carousel.listing} className={`${styles.viewBtn} ${viewMode === Carousel.listing ? styles.viewBtnActive : ''}`} onClick={() => onChangeView(Carousel.listing)}>
                                <LayoutGrid size={13} data-sentry-element="LayoutGrid" data-sentry-source-file="ExploreToolbar.tsx" />
                                {t('explore.view.grid')}
                            </button>
                            <button type="button" role="tab" aria-selected={viewMode === Carousel.split} className={`${styles.viewBtn} ${styles.viewBtnSplit} ${viewMode === Carousel.split ? styles.viewBtnActive : ''}`} onClick={() => onChangeView(Carousel.split)}>
                                <Columns2 size={13} data-sentry-element="Columns2" data-sentry-source-file="ExploreToolbar.tsx" />
                                {t('explore.view.split')}
                            </button>
                            <button type="button" role="tab" aria-selected={viewMode === Carousel.fullMap} className={`${styles.viewBtn} ${viewMode === Carousel.fullMap ? styles.viewBtnActive : ''}`} onClick={() => onChangeView(Carousel.fullMap)}>
                                <MapIcon size={13} data-sentry-element="MapIcon" data-sentry-source-file="ExploreToolbar.tsx" />
                                {t('explore.view.map')}
                            </button>
                        </div>
                    </div>
                    </div>
                </div>
            </div>
        </>;
};
export default ExploreToolbar;